import { FC } from "react"
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import {
    DiscountType,
    DisplayLabelType,
    OfferDetailsType,
} from "../../../../../../modules/merchant/admin/products/productsTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { TagRounded, TagSize } from "./Tag"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"

type StyleProps = Readonly<{
    top?: string
    right?: string
    left?: string
    bottom?: string
}>

type OfferTagProps = StyleProps & {
    offerDetails?: OfferDetailsType
    size?: TagSize
}

const useStyles = makeStyles<StyleProps>()((_theme, { top, right, left, bottom }) => ({
    container: {
        position: "absolute",
        ...(top ? { top } : {}),
        ...(bottom ? { bottom } : {}),
        ...(left ? { left } : {}),
        ...(right ? { right } : {}),
    },
    saleMarkText: {
        color: _theme.palette.common.white,
        textAlign: "center",
        lineHeight: 1.1,
        fontWeight: 500,
    },
    smTypography: {
        fontSize: "0.625rem",
        lineHeight: "150%",
        letterSpacing: "0.4px",
        fontWeight: 500,
    },
    tag: {
        position: "absolute",
        top: 0,
        right: 0,
    },
    saleMark: {
        background: _theme.palette.tailwind.red[600],
        padding: "0.5rem 0.75rem",
        borderRadius: "50%",
        width: "3.125rem",
        height: "3.125rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}))

export const OfferTag: FC<OfferTagProps> = ({ offerDetails, top, left, right, bottom, size = "md" }) => {
    const { classes } = useStyles({ top, right, left, bottom })
    const moneyFactory = useMoney({})

    if (!offerDetails) return null

    const { discount } = offerDetails

    if (
        !discount ||
        (discount?.displayLabelType &&
            [DisplayLabelType.OLD_NEW_PRICE, DisplayLabelType.LIMITED_ON_SALE].includes(discount?.displayLabelType))
    ) {
        return null
    }

    const typographyProps =
        size === "sm"
            ? { variant: "body1" as const, className: classes.smTypography }
            : { variant: "body1" as const }

    const getTagText = () => {
        switch (discount.displayLabelType) {
            case DisplayLabelType.SAVE_X:
                if (discount.type === DiscountType.DISCOUNT_PERCENTAGE) {
                    return (
                        <LocalizedStrict
                            id="offer-tag-save-x-percentage"
                            vars={{ amount: discount?.percentage?.amount || 0 }}
                        >
                            <Typography {...typographyProps}>
                                {`${discount?.percentage?.amount}% off / Spar ${discount?.percentage?.amount}%`}
                            </Typography>
                        </LocalizedStrict>
                    )
                } else if (discount.type === DiscountType.DISCOUNT_PRICE) {
                    const formattedAmount = discount.price ? moneyFactory.formatAsNumber(toDinero(discount.price)) : 0
                    return (
                        <LocalizedStrict id="offer-tag-save-x-price" vars={{ amount: formattedAmount }}>
                            <Typography {...typographyProps}>{`Save ${formattedAmount} kr`}</Typography>
                        </LocalizedStrict>
                    )
                }
                return null
            case DisplayLabelType.GOOD_PRICE:
                return (
                    <LocalizedStrict id="offer-tag-good-price">
                        <Typography {...typographyProps}>Good price</Typography>
                    </LocalizedStrict>
                )
            case DisplayLabelType.GREAT_OFFER:
                return (
                    <LocalizedStrict id="offer-tag-great-offer">
                        <Typography {...typographyProps}>Great offer</Typography>
                    </LocalizedStrict>
                )
            case DisplayLabelType.BUY_NOW:
                return (
                    <LocalizedStrict id="offer-tag-buy-now">
                        <Typography {...typographyProps}>Buy now</Typography>
                    </LocalizedStrict>
                )
            case DisplayLabelType.DISCOUNT:
                return (
                    <LocalizedStrict id="offer-tag-discount">
                        <Typography {...typographyProps}>Discount</Typography>
                    </LocalizedStrict>
                )
            case DisplayLabelType.WEEK_OFFER:
                return (
                    <LocalizedStrict id="offer-tag-week-offer">
                        <Typography {...typographyProps}>Week's offer</Typography>
                    </LocalizedStrict>
                )
            case DisplayLabelType.DAY_OFFER:
                return (
                    <LocalizedStrict id="offer-tag-day-offer">
                        <Typography {...typographyProps}>Today's offer</Typography>
                    </LocalizedStrict>
                )
            default:
                return null
        }
    }

    const tagText = getTagText()
    if (!tagText) return null

    return (
        <Box className={classes.container}>
            <TagRounded size={size}>{tagText}</TagRounded>
        </Box>
    )
}
